import { FormEvent, useState } from 'react'
import { Lock, SpinnerGap, MapPin, Star, Shield, Clock, Users, CheckCircle, MagnifyingGlass, Trophy, ArrowDown, Phone, TrendUp, Calendar, Eye, CaretDown } from '@phosphor-icons/react'
import Logo from '@/components/Logo'
import { SEO } from '@/components/SEO'
import { motion } from 'framer-motion'
import { DEMO_ZIPS, DEMO_SERVICE_TYPES } from '@/lib/demoData'

const PASSWORD_HASH = import.meta.env.VITE_PREVIEW_PASSWORD_HASH || '739c495eb90f05ed919559c8f8a0cc8dbb552898fb4bf474079d69713273882d'

interface SiteLaunchGateProps {
  onUnlock: () => void
  onDemoSearch?: (zip: string, service?: string) => void
}

async function sha256Hex(value: string) {
  const bytes = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((part) => part.toString(16).padStart(2, '0'))
    .join('')
}

/* ── static demo data ─────────────────────────────────── */
const DEMO_CONTRACTORS = [
  { name: 'Precision Sealcoat & Paving', city: 'Dallas, TX', rating: 4.9, reviews: 127, tier: 'Diamond' },
  { name: 'All-Star Asphalt Co.', city: 'Orlando, FL', rating: 4.8, reviews: 98, tier: 'Diamond' },
  { name: 'Pacific Paving Pros', city: 'Portland, OR', rating: 4.9, reviews: 84, tier: 'Diamond' },
  { name: 'Anchor Paving Co.', city: 'Anchorage, AK', rating: 4.7, reviews: 63, tier: 'Diamond' },
]

const DEMO_SERVICES = [
  { name: 'Sealcoating', icon: Shield, description: 'Connect with trusted sealcoating professionals in your area', color: 'from-blue-500 to-blue-600' },
  { name: 'Crack Filling', icon: CheckCircle, description: 'Find experts for professional crack sealing and repair', color: 'from-green-500 to-green-600' },
  { name: 'Striping & Line Marking', icon: Star, description: 'Connect with professional striping and marking specialists', color: 'from-yellow-500 to-orange-500' },
  { name: 'Asphalt Repair', icon: Clock, description: 'Find skilled professionals for asphalt repair and restoration', color: 'from-red-500 to-red-600' },
  { name: 'Resurfacing', icon: Users, description: 'Connect with resurfacing and overlay specialists', color: 'from-purple-500 to-purple-600' },
  { name: 'Cost Estimator', icon: Calendar, description: 'Get general cost estimates to help plan your project budget', color: 'from-emerald-500 to-emerald-600' },
]

const DEMO_STATS = [
  { icon: Users, value: '470+', label: 'Listed Contractors' },
  { icon: Star, value: '4.5+', label: 'Min Rating Threshold' },
  { icon: Clock, value: '24hr', label: 'Response Goal' },
  { icon: CheckCircle, value: '50', label: 'States Covered' },
]

const DEMO_STEPS = [
  { number: '01', title: 'Tell Us Your Needs', description: 'Enter your ZIP code and describe your project. Takes less than 2 minutes.', icon: MapPin, color: 'from-blue-500 to-blue-600' },
  { number: '02', title: 'Get Matched', description: 'Our algorithm finds 3 pre-screened contractors in your area within 24 hours.', icon: Users, color: 'from-green-500 to-green-600' },
  { number: '03', title: 'Compare & Choose', description: 'Review profiles, ratings, and get quotes. Contact your preferred contractor directly.', icon: Star, color: 'from-yellow-500 to-orange-500' },
  { number: '04', title: 'Get It Done', description: 'Your chosen contractor completes the work. Rate your experience to help others.', icon: CheckCircle, color: 'from-purple-500 to-purple-600' },
]

/* ── component ────────────────────────────────────────── */
export function SiteLaunchGate({ onUnlock, onDemoSearch }: SiteLaunchGateProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [unlockOpen, setUnlockOpen] = useState(false)
  const [demoZip, setDemoZip] = useState('')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      if (!PASSWORD_HASH) {
        setError('Preview password is not configured.')
        return
      }
      const enteredHash = await sha256Hex(password.trim())
      if (enteredHash !== PASSWORD_HASH) {
        setError('Incorrect password.')
        return
      }
      onUnlock()
    } catch {
      setError('Unable to unlock preview right now.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <SEO
        title="Website Launching Soon | SealcoatPaving.com"
        description="SealcoatPaving.com is preparing for launch. Preview the full site experience — find trusted sealcoating and paving contractors near you."
        path="/"
        noindex
      />

      {/* The splash keeps navigation private until the preview is unlocked. */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-card/95 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 outline-none" tabIndex={-1} aria-label="Home">
              <Logo size={40} />
            </button>
            <div className="flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-500/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-black">
              <Eye size={12} weight="bold" /> Splash
            </div>
          </div>
          <button
            onClick={() => setUnlockOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent/90"
          >
            <Lock size={14} weight="bold" /> Unlock Site
          </button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════
          HERO SECTION — mirrors the real HomePage hero
         ═══════════════════════════════════════════════════ */}
      <section className="relative flex min-h-[90vh] items-center overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        {/* Floating particles (decorative only) */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          {[...Array(5)].map((_, i) => (
            <motion.div key={i} className="absolute h-3 w-3 rounded-full bg-primary/30 shadow-lg"
              animate={{ x: [0, 80, 0], y: [0, -80, 0], opacity: [0.3, 0.9, 0.3] }}
              transition={{ duration: 8 + i * 2, repeat: Infinity, delay: i * 1.2 }}
              style={{ left: `${20 + i * 15}%`, top: `${30 + i * 10}%` }}
            />
          ))}
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          {/* Left column — headline + search */}
          <motion.div className="space-y-10 lg:col-span-7 lg:col-start-2"
            initial={{ opacity: 0, x: -80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="space-y-6">
              <span className="brand-wordmark inline-block rounded-full border border-border bg-secondary px-4 py-2 text-[0.72rem] tracking-[0.24em]">
                🏆 #1 Contractor Referral Network
              </span>
              <h1 className="text-5xl font-bold leading-tight lg:text-7xl">
                <span className="block text-foreground">Find</span>
                <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Trusted Pros</span>
                <span className="block text-4xl text-foreground/80 lg:text-5xl">Near You</span>
              </h1>
              <p className="max-w-2xl text-xl leading-relaxed text-muted-foreground lg:text-2xl">
                <strong>We connect you with verified contractors.</strong> SealcoatPaving.com is a trusted referral service
                that matches property owners with pre-screened sealcoating and paving professionals in your area.
              </p>
            </div>

            {/* Search box — demo ZIP dropdown */}
            <div className="rounded-2xl border border-border/50 bg-card/50 p-8 shadow-2xl backdrop-blur-lg">
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="relative flex-1">
                  <MapPin size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
                  <select
                    value={demoZip}
                    onChange={e => setDemoZip(e.target.value)}
                    className="h-16 w-full appearance-none rounded-xl border-0 bg-background/50 pl-12 pr-10 text-lg outline-none focus:ring-2 focus:ring-accent/30"
                  >
                    <option value="">Select a ZIP Code</option>
                    {DEMO_ZIPS.map(z => (
                      <option key={z.zip} value={z.zip}>
                        {z.zip} — {z.city}, {z.state}
                      </option>
                    ))}
                  </select>
                  <CaretDown size={18} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                </div>
                <button
                  onClick={() => demoZip && onDemoSearch?.(demoZip)}
                  disabled={!demoZip}
                  className="h-16 rounded-xl bg-gradient-to-r from-accent to-accent/90 px-12 font-semibold text-white shadow-lg transition hover:from-accent/90 hover:to-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Find Contractors
                </button>
              </div>
              <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <span className="text-sm text-muted-foreground">Try a demo search — 10 sample locations available</span>
              </div>
            </div>
          </motion.div>

          {/* Right column — brand visual + floating cards */}
          <motion.div className="relative lg:col-span-4 lg:col-start-9"
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.8 }}>
            {/* Floating badge: Verified Pro */}
            <motion.div className="absolute -left-6 -top-6 rounded-lg border border-border bg-card p-4 shadow-lg"
              animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20">
                  <CheckCircle size={16} className="text-green-500" />
                </div>
                <span className="text-sm font-medium">Verified Pro</span>
              </div>
            </motion.div>
            {/* Floating badge: Rating */}
            <motion.div className="absolute -bottom-4 -right-4 rounded-lg border border-border bg-card p-4 shadow-lg"
              animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1 }}>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500/20">
                  <Star size={16} className="text-yellow-500" />
                </div>
                <span className="text-sm font-medium">4.9 Rating</span>
              </div>
            </motion.div>

            {/* Main hero image */}
            <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-neutral-950 shadow-2xl">
              <img
                src="/sealcoatpaving_enterprise_brand_system/01_masters/B214451F-12EF-4356-9F67-797F2682D2B3.jpeg"
                alt="SealcoatPaving branded hero"
                className="block h-auto w-full"
                loading="eager"
              />
              <div className="bg-gradient-to-t from-black via-black/90 to-neutral-950 px-6 py-5 text-white">
                <div className="flex items-end justify-between gap-4">
                  <div className="space-y-1">
                    <div className="brand-wordmark text-[0.68rem] tracking-[0.24em] text-amber-300">SealcoatPaving.com</div>
                    <div className="text-lg font-semibold leading-tight sm:text-xl">Professional asphalt sealcoating and paving</div>
                  </div>
                  <div className="rounded-2xl border border-amber-400/20 bg-white/5 px-4 py-3 text-right backdrop-blur-sm">
                    <div className="text-2xl font-bold">24hr</div>
                    <div className="brand-wordmark text-[0.6rem] tracking-[0.28em] text-white/70">Response Goal</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <span className="text-sm">Scroll to explore</span>
            <ArrowDown size={20} />
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════
          STATS BAR
         ═══════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 sm:px-6 lg:grid-cols-4 lg:px-8">
          {DEMO_STATS.map((stat, i) => (
            <motion.div key={i} className="text-center"
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}>
              <motion.div className="relative mx-auto mb-6 h-24 w-24"
                animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 blur-lg" />
                <div className="relative flex h-full w-full items-center justify-center rounded-2xl border border-border bg-card shadow-lg">
                  <stat.icon size={32} className="text-primary" />
                </div>
              </motion.div>
              <div className="mb-2 text-4xl font-bold text-foreground">{stat.value}</div>
              <div className="font-medium text-muted-foreground">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SERVICES GRID
         ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 -skew-y-3 scale-110 bg-gradient-to-br from-secondary/30 to-muted/30" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div className="mb-16 space-y-4 text-center"
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <h2 className="text-5xl font-bold text-foreground">Our <span className="text-primary">Services</span></h2>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
              Find trusted contractors for comprehensive asphalt and paving solutions through our verified referral network
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DEMO_SERVICES.map((svc, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
                <button
                  onClick={() => onDemoSearch?.('', DEMO_SERVICE_TYPES[i]?.key)}
                  className="group relative w-full overflow-hidden rounded-2xl border-0 bg-card/90 p-8 text-left shadow-xl backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${svc.color} shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                    <svc.icon size={32} className="text-white" />
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-foreground transition-colors group-hover:text-primary">{svc.name}</h3>
                  <p className="leading-relaxed text-muted-foreground">{svc.description}</p>
                  <div className={`mt-6 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r ${svc.color} px-4 py-2 text-sm font-medium text-white shadow`}>
                    Free Referral
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          TOP CONTRACTORS — "Best of USA"
         ═══════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 space-y-4 text-center">
            <div className="mx-auto mb-4 inline-flex items-center justify-center rounded-full bg-amber-100 p-3">
              <Trophy size={32} className="text-amber-600" weight="duotone" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight">Best of USA — Top Paving Pros</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              We rigorously cross-reference public data sources to curate the most reliable, highly-rated Diamond-Level paving and sealcoating contractors across America.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {DEMO_CONTRACTORS.map((c, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="group relative h-full overflow-hidden rounded-xl border border-border bg-card p-5 shadow transition-all hover:border-amber-400 hover:shadow-lg">
                  <div className="absolute -mr-8 -mt-8 right-0 top-0 h-16 w-16 rounded-full bg-gradient-to-bl from-amber-200 to-transparent opacity-50 transition-transform group-hover:scale-150" />
                  <div className="flex flex-col justify-between gap-4 h-full">
                    <div className="space-y-2">
                      <span className="inline-block rounded-full border-0 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Rank #{i + 1}
                      </span>
                      <h3 className="text-lg font-bold leading-tight transition-colors group-hover:text-amber-600">{c.name}</h3>
                    </div>
                    <div className="mt-auto space-y-2">
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <MapPin size={16} />
                        {c.city}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center text-amber-500">
                          <Star size={16} weight="fill" />
                          <span className="ml-1 font-bold">{c.rating}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">({c.reviews} verified reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          HOW IT WORKS
         ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div className="mb-16 space-y-4 text-center"
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <h2 className="text-5xl font-bold text-foreground">How It <span className="text-primary">Works</span></h2>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
              Get connected with trusted contractors in 4 simple steps
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-3xl gap-10">
            {DEMO_STEPS.map((step, i) => (
              <motion.div key={i} className="relative"
                initial={{ opacity: 0, y: 80 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}>
                <div className="relative mx-auto max-w-lg rounded-2xl border border-border/50 bg-card/80 p-8 shadow-lg backdrop-blur-sm">
                  <div className="absolute -top-6 left-8 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
                    <span className="text-lg font-bold text-white">{step.number}</span>
                  </div>
                  <div className={`mx-auto mb-6 mt-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${step.color}`}>
                    <step.icon size={32} className="text-white" />
                  </div>
                  <div className="space-y-3 text-center">
                    <h3 className="text-xl font-bold text-foreground">{step.title}</h3>
                    <p className="leading-relaxed text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          CONTRACTOR CTA SECTION
         ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5" />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div className="space-y-8"
            initial={{ opacity: 0, x: -80 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="space-y-6">
              <span className="inline-block rounded-full border border-accent/20 bg-accent/20 px-4 py-2 text-sm font-medium text-accent">
                For Contractors
              </span>
              <h2 className="text-5xl font-bold leading-tight text-foreground">
                Grow Your
                <span className="block text-accent">Paving Business</span>
              </h2>
              <p className="text-xl leading-relaxed text-muted-foreground">
                Join our exclusive network of verified contractors and get premium leads from property owners in your service area. Increase your revenue with qualified referrals.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Users, value: '500+', label: 'Active Contractors' },
                { icon: TrendUp, value: '30%', label: 'Avg Revenue Increase' },
                { icon: Star, value: '4.9', label: 'Customer Rating' },
                { icon: Clock, value: '24hr', label: 'Lead Response' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/20">
                    <s.icon size={24} className="text-accent" />
                  </div>
                  <div className="text-2xl font-bold text-accent">{s.value}</div>
                  <div className="text-sm text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <button disabled className="rounded-xl bg-gradient-to-r from-accent to-accent/90 px-8 py-4 text-lg font-semibold text-white shadow-lg">
                Join Our Network
              </button>
              <button disabled className="rounded-xl border border-accent px-8 py-4 text-lg font-semibold text-accent">
                Learn More
              </button>
            </div>
          </motion.div>

          <motion.div className="space-y-6"
            initial={{ opacity: 0, x: 80 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            {[
              { icon: Shield, title: 'Verified Badge', description: 'Get your professional verification badge and stand out from the competition', color: 'from-blue-500 to-blue-600' },
              { icon: Users, title: 'Quality Leads', description: 'Receive pre-screened leads from property owners actively seeking your services', color: 'from-green-500 to-green-600' },
              { icon: TrendUp, title: 'Business Growth', description: 'Expand your customer base and increase revenue with our referral platform', color: 'from-purple-500 to-purple-600' },
            ].map((b, i) => (
              <motion.div key={i} className="rounded-2xl border border-border/50 bg-card/80 p-6 shadow-lg backdrop-blur-sm"
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
                <div className="flex items-start gap-4">
                  <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${b.color}`}>
                    <b.icon size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-foreground">{b.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{b.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          CTA SECTION — Ready to find your contractor?
         ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-[0.06]" />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div className="space-y-8"
            initial={{ opacity: 0, x: -80 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <h2 className="text-5xl font-bold leading-tight text-foreground">
              Ready to Find Your
              <span className="block text-primary">Perfect Contractor?</span>
            </h2>
            <p className="text-xl leading-relaxed text-muted-foreground">
              Join thousands of satisfied customers who found quality contractors through our trusted referral network. Get matched today.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <button disabled className="rounded-xl bg-accent px-8 py-4 text-lg font-semibold text-white shadow-lg">
                Get Started Today
              </button>
              <button disabled className="rounded-xl border border-primary px-8 py-4 text-lg font-semibold text-primary">
                Learn More
              </button>
            </div>
          </motion.div>

          <motion.div className="relative"
            initial={{ opacity: 0, x: 80 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="mx-auto max-w-md">
              <div className="overflow-hidden rounded-3xl border border-white/10 bg-black shadow-2xl">
                <img
                  src="/sealcoatpaving_enterprise_brand_system/01_masters/85904B45-9348-4117-A03F-62BF51DFB059.jpeg"
                  alt="SealcoatPaving brand impact artwork"
                  className="block h-auto w-full"
                  loading="lazy"
                />
                <div className="bg-gradient-to-t from-black via-black/90 to-transparent px-6 py-5 text-white">
                  <div className="text-3xl font-bold">470+</div>
                  <div className="brand-wordmark text-xs tracking-[0.28em] text-white/70">Trusted Contractors</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          FOOTER (replica)
         ═══════════════════════════════════════════════════ */}
      <footer className="border-t border-border/50 bg-muted/30 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-4">
              <img
                src="/sealcoatpaving_enterprise_brand_system/01_masters/42230D2C-F05D-42B3-B09F-68A2D4585542.png"
                alt="SealcoatPaving logo"
                className="h-16 w-auto object-contain"
              />
              <p className="leading-relaxed text-muted-foreground">
                Connecting property owners with trusted paving professionals through our referral network.
              </p>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                <span className="text-sm text-muted-foreground">Always available</span>
              </div>
            </div>
            {[
              { title: 'Services', links: ['Sealcoating', 'Crack Filling', 'Asphalt Repair', 'Striping'] },
              { title: 'Company', links: ['About Us', 'How It Works', 'Browse by State', 'Become a Contractor', 'Contact', 'FAQ'] },
              { title: 'Legal', links: ['Terms of Service', 'Privacy Policy', 'Cookie Policy'] },
            ].map((sec, i) => (
              <div key={i} className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">{sec.title}</h4>
                <div className="space-y-2">
                  {sec.links.map((l) => (
                    <span key={l} className="block cursor-default text-muted-foreground">{l}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-border/50 pt-8 text-center text-muted-foreground space-y-1">
            <p>Contractor data sourced from © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">OpenStreetMap</a> contributors (<a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">ODbL</a>).</p>
            <p>© 2025 SealcoatPaving.com. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ═══════════════════════════════════════════════════
          STICKY UNLOCK BAR — bottom of viewport
         ═══════════════════════════════════════════════════ */}
      {!unlockOpen && (
        <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-border/60 bg-card/95 px-4 py-3 shadow-[0_-4px_24px_rgba(0,0,0,0.12)] backdrop-blur-lg sm:px-6">
          <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="flex items-center gap-3">
              <Lock size={18} className="text-accent" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Locked splash</strong> — Unlock the full site with the access password.
              </p>
            </div>
            <button
              onClick={() => setUnlockOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-accent/90"
            >
              <Lock size={14} weight="bold" /> Unlock Site
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          UNLOCK PANEL (overlay)
         ═══════════════════════════════════════════════════ */}
      {unlockOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setUnlockOpen(false) }}>
          <motion.div className="w-full max-w-md rounded-[2rem] border border-border/50 bg-card p-8 shadow-2xl"
            initial={{ opacity: 0, scale: 0.92, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <div className="mb-6 flex items-center justify-between">
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-accent">
                <Lock size={14} /> Launch Access
              </div>
              <button onClick={() => setUnlockOpen(false)} className="text-sm text-muted-foreground hover:text-foreground" aria-label="Close">✕</button>
            </div>

            <h2 className="text-2xl font-bold text-foreground">Unlock Full Site</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Enter the preview password to leave demo mode and access all live routes, contractor profiles, and interactive features.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-foreground" htmlFor="site-password">
                Password
              </label>
              <input
                id="site-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full rounded-2xl border border-border bg-background px-4 py-4 text-base text-foreground outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <button
                type="submit"
                disabled={isSubmitting || !password.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-accent px-4 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? <SpinnerGap size={18} className="animate-spin" /> : <Lock size={18} />}
                Unlock Site
              </button>
            </form>
            <p className="mt-4 text-center text-xs uppercase tracking-[0.18em] text-muted-foreground/60">
              Authorized access only
            </p>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default SiteLaunchGate
